import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  getStoredConsent,
  setStoredConsent,
  type CookieConsentConfig,
  type CookieConsentState,
  type CookieConsentCategory,
} from './types';
import { CookiePreferencesModal } from './CookiePreferencesModal';

const DEFAULT_CONSENT: CookieConsentState = {
  necessary: true,
  analytics: false,
  marketing: false,
  timestamp: '',
};

const DEFAULT_CATEGORIES: CookieConsentCategory[] = [
  { id: 'necessary', label: 'Necessary', description: 'Required for the site to work', required: true },
  { id: 'analytics', label: 'Analytics', description: 'Help us understand how you use the site', required: false },
  { id: 'marketing', label: 'Marketing', description: 'Used for advertising', required: false },
];

interface CookieConsentWidgetProps {
  brandId: string;
  config?: CookieConsentConfig | null;
  isLoading?: boolean;
}

export function CookieConsentWidget({ brandId, config, isLoading }: CookieConsentWidgetProps) {
  const [consent, setConsent] = useState<CookieConsentState | null>(null);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (mounted && brandId) {
      setConsent(getStoredConsent(brandId));
    }
  }, [mounted, brandId]);

  const settings = config?.settings ?? {};
  const bannerEnabled = settings.banner_enabled !== false;
  const categories = (settings.categories && settings.categories.length > 0)
    ? settings.categories
    : DEFAULT_CATEGORIES;
  const primaryColor = settings.primary_color || '#2563eb';

  const currentConsent: CookieConsentState = consent ?? DEFAULT_CONSENT;

  const acceptAll = () => {
    const next: CookieConsentState = {
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString(),
    };
    setStoredConsent(brandId, next);
    setConsent(next);
  };

  const rejectNonEssential = () => {
    const next: CookieConsentState = {
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString(),
    };
    setStoredConsent(brandId, next);
    setConsent(next);
  };

  const handleSavePreferences = (next: CookieConsentState) => {
    setStoredConsent(brandId, next);
    setConsent(next);
  };

  if (!mounted || !brandId || isLoading || consent !== null) {
    return null;
  }

  if (!bannerEnabled) return null;

  const position = settings.banner_position === 'modal' ? 'modal' : 'bottom';
  const bannerText = settings.banner_text || 'We use cookies to improve your experience. By continuing you accept our use of cookies.';
  const acceptLabel = settings.accept_all_label || 'Accept all';
  const rejectLabel = settings.reject_non_essential_label || 'Reject non-essential';
  const customizeLabel = settings.customize_label || 'Customize';
  const policyUrl = settings.policy_url?.trim();

  const actions = (
    <div className="flex flex-wrap items-center gap-2">
      {policyUrl && (
        <Button
          variant="ghost"
          size="sm"
          className="text-primary underline-offset-4 hover:underline"
          asChild
        >
          <a href={policyUrl} target="_blank" rel="noopener noreferrer">
            Cookie policy
          </a>
        </Button>
      )}
      <Button
        variant="outline"
        size="sm"
        onClick={() => setPrefsOpen(true)}
      >
        {customizeLabel}
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={rejectNonEssential}
      >
        {rejectLabel}
      </Button>
      <Button
        size="sm"
        onClick={acceptAll}
        style={{ backgroundColor: primaryColor }}
      >
        {acceptLabel}
      </Button>
    </div>
  );

  if (position === 'modal') {
    return (
      <>
        <div
          className="fixed inset-0 z-[9998] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          <div className="bg-background text-foreground rounded-lg shadow-lg max-w-md w-full p-6 space-y-4">
            <p className="text-sm">{bannerText}</p>
            {actions}
          </div>
        </div>
        <CookiePreferencesModal
          open={prefsOpen}
          onOpenChange={setPrefsOpen}
          categories={categories}
          initialConsent={currentConsent}
          primaryColor={primaryColor}
          onSave={handleSavePreferences}
        />
      </>
    );
  }

  return (
    <>
      <div
        className="fixed bottom-0 left-0 right-0 z-[9998] border-t bg-background text-foreground p-4 shadow-lg"
        role="dialog"
        aria-label="Cookie consent"
      >
        <div className="container mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-sm flex-1">{bannerText}</p>
          {actions}
        </div>
      </div>
      <CookiePreferencesModal
        open={prefsOpen}
        onOpenChange={setPrefsOpen}
        categories={categories}
        initialConsent={currentConsent}
        primaryColor={primaryColor}
        onSave={handleSavePreferences}
      />
    </>
  );
}
